import { useCallback, useEffect, useState } from "react";
import api, { apiError, runJob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Sparkles, Globe, Trash2, Loader2, TrendingUp, ShieldCheck, Lightbulb, Tag, FileText, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/brand";

const PRIO = { high: "bg-red-50 text-red-700", medium: "bg-amber-50 text-amber-700", low: "bg-slate-100 text-slate-600" };

function MasterReport({ r }) {
  const d = r?.data;
  if (!d) return null;
  const lists = [["Keyword targets", d.keyword_targets, "kw"], ["Local SEO", d.local_seo], ["Link building", d.link_building], ["Lead generation", d.lead_gen], ["KPIs", d.kpis]];
  return (
    <div className="mt-4 space-y-5 border-t border-slate-100 pt-4" data-testid="master-report">
      <p className="text-sm text-slate-700">{d.overview}</p>
      {d.current_position && <p className="text-[13px] text-slate-500"><b>Current position:</b> {d.current_position}</p>}
      {d.quick_wins?.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-emerald-600 mb-2">Quick wins</p>
          <div className="space-y-1.5">{d.quick_wins.map((q, i) => (
            <div key={i} className="flex items-start gap-2 text-[13px] text-slate-700"><span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${PRIO[q.impact] || PRIO.low}`}>{q.impact}</span>{q.action}</div>))}</div>
        </div>
      )}
      {(d.phases || []).map((p, i) => (
        <div key={i} className="rounded-md border border-slate-200 p-3.5">
          <div className="flex items-center justify-between mb-2"><p className="font-display font-semibold text-slate-900 text-sm">{p.phase}</p><span className="text-[11px] text-slate-400">{p.timeframe}</span></div>
          {p.goal && <p className="text-[12px] text-slate-500 mb-2">{p.goal}</p>}
          <div className="space-y-1.5">{(p.steps || []).map((s, j) => (
            <div key={j} className="flex items-start gap-2 text-[13px] text-slate-700" data-testid="master-step">
              <ChevronRight size={14} className="mt-0.5 shrink-0" style={{ color: "var(--tenant-primary)" }} />
              <span className="flex-1">{s.action}{s.why && <span className="text-slate-400"> — {s.why}</span>}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0">{s.category}</span>
            </div>))}</div>
        </div>))}
      {d.content_plan?.length > 0 && (
        <div><p className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Content plan</p>
          <ul className="text-[12px] text-slate-600 space-y-0.5">{d.content_plan.map((c, i) => <li key={i}>• [{c.type}] {c.title}{c.intent ? ` (${c.intent})` : ""}</li>)}</ul></div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {lists.map(([title, arr, kw]) => arr?.length ? (
          <div key={title}><p className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">{title}</p>
            {kw ? <div className="flex flex-wrap gap-1.5">{arr.map((k, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-orange-50 text-orange-700">{k}</span>)}</div>
              : <ul className="text-[12px] text-slate-600 space-y-0.5">{arr.map((c, i) => <li key={i}>• {c}</li>)}</ul>}
          </div>) : null)}
      </div>
    </div>
  );
}

function Analysis({ a }) {
  if (!a) return null;
  const recs = a.recommendations || [];
  const cats = [...new Set(recs.map((r) => r.category))];
  return (
    <div className="mt-4 space-y-4 border-t border-slate-100 pt-4" data-testid="competitor-analysis">
      <p className="text-sm text-slate-700">{a.summary}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-2"><TrendingUp size={13} /> Their strengths</p>
          <ul className="space-y-1.5">{(a.competitor_strengths || []).map((s, i) => <li key={i} className="text-[13px] text-slate-600 flex gap-2"><span className="text-slate-300">•</span>{s}</li>)}</ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-emerald-600 flex items-center gap-1.5 mb-2"><ShieldCheck size={13} /> Our advantages</p>
          <ul className="space-y-1.5">{(a.our_advantages || []).map((s, i) => <li key={i} className="text-[13px] text-slate-600 flex gap-2"><span className="text-emerald-400">•</span>{s}</li>)}</ul>
        </div>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-2"><Lightbulb size={13} /> Recommendations — what we can do better</p>
        <div className="space-y-2">
          {cats.map((cat) => (
            <div key={cat}>
              <p className="text-[11px] font-semibold text-slate-500 mb-1">{cat}</p>
              {recs.filter((r) => r.category === cat).map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-[13px] text-slate-700 mb-1" data-testid="rec-item">
                  <ChevronRight size={14} className="mt-0.5 shrink-0" style={{ color: "var(--tenant-primary)" }} />
                  <span className="flex-1">{r.action}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${PRIO[r.priority] || PRIO.low}`}>{r.priority}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-2"><Tag size={13} /> Keyword ideas</p>
          <div className="flex flex-wrap gap-1.5">{(a.keyword_ideas || []).map((k, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{k}</span>)}</div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-2"><FileText size={13} /> Blog ideas</p>
          <ul className="space-y-1">{(a.blog_ideas || []).slice(0, 8).map((b, i) => <li key={i} className="text-[12px] text-slate-600">• {b}</li>)}</ul>
        </div>
      </div>
    </div>
  );
}

export default function Competitors() {
  const { user } = useAuth();
  const isStaff = user.role !== "client";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(isStaff ? "" : user.client_id);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", domain: "" });
  const [analyzing, setAnalyzing] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [master, setMaster] = useState(null);
  const [genMaster, setGenMaster] = useState(false);

  useEffect(() => {
    if (isStaff) api.get("/clients").then((r) => { setClients(r.data); if (r.data[0]) setClientId(r.data[0].id); });
  }, [isStaff]);
  const load = useCallback((cid) => { if (cid) api.get(`/clients/${cid}/competitors`).then((r) => setItems(r.data)); }, []);
  useEffect(() => {
    load(clientId);
    setSuggestions([]);
    if (clientId) api.get(`/clients/${clientId}/master-report`).then((r) => setMaster(r.data)).catch(() => {});
  }, [clientId, load]);

  const generateMaster = async () => {
    setGenMaster(true);
    try {
      const data = await runJob(api.post(`/clients/${clientId}/master-report`));
      setMaster(data);
      toast.success("Master report ready");
    } catch (e) { toast.error(e.message || "Failed"); }
    finally { setGenMaster(false); }
  };

  const add = async (name, domain) => {
    try {
      await api.post(`/clients/${clientId}/competitors`, { name, domain });
      toast.success("Competitor added");
      setOpen(false); setForm({ name: "", domain: "" });
      load(clientId);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const data = await runJob(api.post(`/clients/${clientId}/competitors/suggest`));
      setSuggestions(data.competitors || []);
    } catch (e) { toast.error(e.message || "Failed"); }
    finally { setSuggesting(false); }
  };

  const analyze = async (id) => {
    setAnalyzing(id);
    try {
      const data = await runJob(api.post(`/clients/${clientId}/competitors/${id}/analyze`));
      setItems((arr) => arr.map((c) => (c.id === id ? data : c)));
      toast.success("Analysis ready");
    } catch (e) { toast.error(e.message || "Analysis failed"); }
    finally { setAnalyzing(null); }
  };

  const del = async (id) => { await api.delete(`/competitors/${id}`); load(clientId); };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Competitor Intelligence</h1>
          <p className="text-slate-500 text-sm mt-1">Local rivals, what they're doing & how to beat them</p>
        </div>
        {isStaff && (
          <div className="flex items-center gap-2">
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="w-48" data-testid="comp-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" onClick={suggest} disabled={suggesting} data-testid="suggest-btn">
              {suggesting ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Sparkles size={15} className="mr-1.5" />} AI Suggest
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="add-competitor-btn"><Plus size={16} className="mr-1" /> Add</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display">Add competitor</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="comp-name-input" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Website domain</Label><Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="competitor.com" className="mt-1.5" data-testid="comp-domain-input" /></div>
                </div>
                <DialogFooter><Button onClick={() => add(form.name, form.domain)} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="submit-competitor-btn">Add</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {suggestions.length > 0 && (
        <Card className="p-4 border-slate-200 bg-slate-50">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5"><Sparkles size={13} /> AI-suggested local competitors</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {suggestions.map((s, i) => (
              <div key={i} className="rounded-md border border-slate-200 bg-white p-3" data-testid="suggestion-card">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-800">{s.name}</p>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => add(s.name, s.domain)} data-testid="add-suggestion-btn">Add</Button>
                </div>
                <p className="text-[11px] text-slate-400">{s.domain}</p>
                <p className="text-[12px] text-slate-500 mt-1">{s.reason}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5 border-slate-200" style={{ borderTop: "3px solid var(--tenant-primary)" }} data-testid="master-report-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles size={18} style={{ color: "var(--tenant-primary)" }} />
            <div>
              <p className="font-display font-semibold text-slate-900">Master SEO Domination Report</p>
              <p className="text-xs text-slate-400">Step-by-step plan to outrank competitors, win leads & capture their traffic</p>
            </div>
          </div>
          <Button onClick={generateMaster} disabled={genMaster || !clientId} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="gen-master-btn">
            {genMaster ? <><Loader2 size={15} className="animate-spin mr-1.5" /> Building…</> : (master ? "Regenerate" : "Generate Report")}
          </Button>
        </div>
        {genMaster && !master && <p className="text-xs text-slate-400 mt-3">Analyzing your market and competitors — this can take up to a minute…</p>}
        {master && <p className="text-[11px] text-slate-400 mt-2">Last generated {fmtDate(master.created_at)}</p>}
        <MasterReport r={master} />
      </Card>

      <div className="space-y-4">
        {items.length === 0 && <Card className="p-12 text-center border-slate-200 text-slate-400 text-sm">No competitors tracked yet.</Card>}
        {items.map((c) => (
          <Card key={c.id} className="p-5 border-slate-200" data-testid="competitor-card">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="h-10 w-10 rounded flex items-center justify-center text-white font-display font-bold" style={{ background: "var(--tenant-primary)" }}>{c.name[0]}</div>
              <div className="min-w-0">
                <h3 className="font-display font-semibold text-slate-900">{c.name}</h3>
                <p className="text-xs text-slate-400 flex items-center gap-1"><Globe size={11} /> {c.domain || "—"}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {c.analyzed_at && <Badge variant="secondary" className="text-[10px]">Analyzed</Badge>}
                {isStaff && (
                  <>
                    <Button size="sm" onClick={() => analyze(c.id)} disabled={analyzing === c.id} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="analyze-btn">
                      {analyzing === c.id ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Analyzing…</> : (c.analysis ? "Re-analyze" : "Analyze")}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(c.id)} data-testid="del-competitor-btn"><Trash2 size={14} className="text-red-500" /></Button>
                  </>
                )}
              </div>
            </div>
            {analyzing === c.id && !c.analysis && <p className="text-xs text-slate-400 mt-3">Crawling sites and analyzing with AI — this can take up to a minute…</p>}
            <Analysis a={c.analysis} />
          </Card>
        ))}
      </div>
    </div>
  );
}
